import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Backdrop,
  Slide,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../styles/theme';
import CustomCard from './CustomCard';
import { router } from '@inertiajs/react';
import PrimaryButton from './PrimaryButton';

interface Listing {
  id: string;
  photo_url?: string | null;
  is_active: boolean;
  address: string;
}

type SwitchListingOverlayProps = {
  open: boolean;
  onClose: () => void;
  listings: Listing[];
  currentListingId: string;
};

const SwitchListingOverlay: React.FC<SwitchListingOverlayProps> = ({ open, onClose, listings, currentListingId }) => {
  // Sort listings: current first, then active, then inactive
  const sortedListings = useMemo(() => {
    const current = listings.find(l => l.id === currentListingId);
    const actives = listings.filter(l => l.id !== currentListingId && l.is_active);
    const inactives = listings.filter(l => l.id !== currentListingId && !l.is_active);
    return [current, ...actives, ...inactives].filter(Boolean) as Listing[];
  }, [listings, currentListingId]);

  const [selectedId, setSelectedId] = useState(currentListingId);

  const handleConfirm = () => {
    onClose();
    router.visit(`/listings/${selectedId}`);
  };

  return (
    <ThemeProvider theme={theme}>
      <Backdrop
        open={open}
        sx={{
          zIndex: theme => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
        onClick={e => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              width: '100%'
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 500,
                bgcolor: 'white',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                p: 3,
                pb: 2,
                minHeight: 400,
                maxHeight: '95vh',
                overflowY: 'auto',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <IconButton
                onClick={onClose}
                sx={{ position: 'absolute', right: 16, top: 16 }}
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, pr: 4 }}>
                Select the listing to view
              </Typography>
              <RadioGroup
                value={selectedId}
                onChange={e => setSelectedId(e.target.value)}
                sx={{ width: '100%', gap: 2 }}

              >
                {sortedListings.map(listing => (
                  <CustomCard
                    key={listing.id}
                    sx={{
                      width: '90%',
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      border: selectedId === listing.id ? '1px solid #000' : '1px solid #eee',
                      boxShadow: selectedId === listing.id ? '0 0 0 1px #0002' : undefined,
                      cursor: 'pointer',
                      minHeight: 64,
                      maxHeight: 120,
                      transition: 'border 0.2s',
                    }}
                    onClick={() => setSelectedId(listing.id)}
                  >
                    <Radio
                      checked={selectedId === listing.id}
                      value={listing.id}
                      sx={{ mt: 0.5, mr: 1 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={selectedId === listing.id ? 600 : 400}>
                        {listing.address.replace(", USA", "")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: listing.is_active ? '#1DBA5C' : '#FF8800',
                          fontWeight: 500,
                          mt: 0.5,
                          display: 'inline-block',
                        }}
                      >
                        {listing.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </CustomCard>
                ))}
              </RadioGroup>
              <PrimaryButton
                fullWidth
                sx={{
                  mt: 16,

                }}
                onClick={handleConfirm}
                disabled={!selectedId}
              >
                Confirm selection
              </PrimaryButton>
            </Box>
          </Box>
        </Slide>
      </Backdrop>
    </ThemeProvider>
  );
};

export default SwitchListingOverlay;